import { Search, Save, Plus, Trash2, TrendingUp, AlertTriangle, Sparkles, BarChart3, Settings2, Globe, User, Clock, RefreshCw, Calendar, Wand2, Check } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/ui-helpers";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useEffect, useState, useCallback, useRef } from "react";

function MapEditor({ title, hint, rows, setRows, kLabel, vLabel, testid }) {
  const add = () => setRows([...rows, { k: "", v: "" }]);
  const set = (i, f, val) => setRows(rows.map((r, idx) => idx === i ? { ...r, [f]: val } : r));
  const del = (i) => setRows(rows.filter((_, idx) => idx !== i));
  return (
    <div className="card-soft p-5" data-testid={testid}>
      <p className="text-sm font-semibold text-slate-800">{title}</p>
      <p className="mb-3 text-xs text-slate-500">{hint}</p>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-2">
            <Input value={r.k} onChange={(e) => set(i, "k", e.target.value)} placeholder={kLabel} data-testid={`${testid}-k-${i}`} />
            <span className="text-slate-400">→</span>
            <Input value={r.v} onChange={(e) => set(i, "v", e.target.value)} placeholder={vLabel} data-testid={`${testid}-v-${i}`} />
            <button onClick={() => del(i)} className="text-slate-400 hover:text-rose-500"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
      <button onClick={add} className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-blue-600" data-testid={`${testid}-add`}><Plus className="h-4 w-4" /> Add</button>
    </div>
  );
}

const timeAgo = (iso) => {
  if (!iso) return "—";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const RANGES = [["today", "Today"], ["week", "7 days"], ["month", "30 days"], ["all", "All time"], ["custom", "Custom"]];

function SearchAnalytics() {
  const [d, setD] = useState(null);
  const [range, setRange] = useState("all");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [live, setLive] = useState(false);
  const [synMap, setSynMap] = useState({});
  const [savingSyn, setSavingSyn] = useState("");
  const timer = useRef(null);

  const load = useCallback((silent) => {
    const p = new URLSearchParams({ limit: "100", range });
    if (range === "custom") { if (start) p.set("start", start); if (end) p.set("end", end); }
    return api.get(`/marketplace/admin/search-logs?${p.toString()}`)
      .then((r) => setD(r.data))
      .catch((e) => { if (!silent) toast.error(formatApiError(e?.response?.data?.detail)); });
  }, [range, start, end]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (timer.current) { clearInterval(timer.current); timer.current = null; }
    if (live) { timer.current = setInterval(() => load(true), 5000); }
    return () => { if (timer.current) clearInterval(timer.current); };
  }, [live, load]);

  const suggestSynonym = async (q) => {
    const target = (synMap[q] || "").trim();
    if (!target) { toast.error("Type the term to map it to first."); return; }
    setSavingSyn(q);
    try {
      await api.post("/marketplace/admin/search-synonym", { term: q, target });
      toast.success(`“${q}” now maps to “${target}” — future searches will resolve.`);
      setSynMap((m) => { const n = { ...m }; delete n[q]; return n; });
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSavingSyn(""); }
  };

  if (!d) return <div className="card-soft p-8 text-center text-sm text-slate-400" data-testid="search-logs-loading">Loading search analytics…</div>;
  const maxPop = Math.max(1, ...(d.popular || []).map((p) => p.count));
  return (
    <div className="space-y-4" data-testid="search-analytics-tab">
      {/* Controls: range + live */}
      <div className="flex flex-wrap items-center gap-2" data-testid="analytics-controls">
        <div className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white p-1" data-testid="range-filter">
          <Calendar className="ml-1.5 h-3.5 w-3.5 text-slate-400" />
          {RANGES.map(([v, l]) => (
            <button key={v} onClick={() => setRange(v)} data-testid={`range-${v}`} className={`rounded-full px-3 py-1 text-xs font-semibold transition ${range === v ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}>{l}</button>
          ))}
        </div>
        {range === "custom" && (
          <div className="inline-flex items-center gap-1.5" data-testid="custom-range">
            <input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="range-start" className="rounded-md border border-slate-200 px-2 py-1 text-xs" />
            <span className="text-slate-400">→</span>
            <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="range-end" className="rounded-md border border-slate-200 px-2 py-1 text-xs" />
          </div>
        )}
        <button onClick={() => setLive((v) => !v)} data-testid="live-toggle" className={`ml-auto inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition ${live ? "bg-emerald-500 text-white" : "border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
          <RefreshCw className={`h-3.5 w-3.5 ${live ? "animate-spin" : ""}`} /> {live ? "Live · updating" : "Go live"}
        </button>
        <button onClick={() => load()} data-testid="refresh-now" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"><RefreshCw className="h-3.5 w-3.5" /> Refresh</button>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="search-logs-kpis">
        <div className="card-soft p-4"><p className="text-xs text-slate-500">Total searches</p><p className="text-2xl font-bold text-slate-800" data-testid="kpi-total">{d.total}</p></div>
        <div className="card-soft p-4"><p className="text-xs text-slate-500">No-result searches</p><p className="text-2xl font-bold text-amber-600" data-testid="kpi-noresult">{d.no_result_total}</p></div>
        <div className="card-soft p-4"><p className="text-xs text-slate-500">Unique keywords</p><p className="text-2xl font-bold text-slate-800" data-testid="kpi-keywords">{(d.popular || []).length}</p></div>
        <div className="card-soft p-4"><p className="text-xs text-slate-500">Top country</p><p className="text-2xl font-bold text-slate-800" data-testid="kpi-country">{(d.countries?.[0]?.country) || "—"}</p></div>
      </div>

      {/* Popular Keywords chart + No results */}
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="card-soft p-5" data-testid="popular-keywords-chart">
          <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800"><BarChart3 className="h-4 w-4 text-emerald-500" /> Popular Keywords</p>
          {(d.popular || []).length ? (
            <div className="space-y-2">
              {d.popular.map((p) => (
                <div key={p.q} className="flex items-center gap-2" data-testid={`popular-kw-${p.q}`}>
                  <span className="w-32 shrink-0 truncate text-xs font-medium text-slate-700" title={p.q}>{p.q}</span>
                  <div className="h-4 flex-1 overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all" style={{ width: `${Math.round((p.count / maxPop) * 100)}%` }} />
                  </div>
                  <span className="w-8 shrink-0 text-right text-xs font-bold text-slate-600">{p.count}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-slate-400">No searches in this range.</p>}
        </div>
        <div className="card-soft p-5" data-testid="no-results-section">
          <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800"><AlertTriangle className="h-4 w-4 text-amber-500" /> Searches with No Results <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-600">market gaps</span></p>
          {(d.no_results || []).length ? d.no_results.map((r) => (
            <div key={r.q} className="border-b border-slate-50 py-2" data-testid={`no-result-${r.q}`}>
              <div className="flex items-center justify-between text-sm">
                <span className="truncate font-medium text-slate-700" title={r.q}>{r.q}</span>
                <span className="ml-2 shrink-0 rounded-full bg-rose-50 px-2 py-0.5 text-xs font-bold text-rose-600">{r.count}×</span>
              </div>
              <div className="mt-1.5 flex items-center gap-1.5" data-testid={`syn-suggest-${r.q}`}>
                <Wand2 className="h-3.5 w-3.5 shrink-0 text-violet-400" />
                <input value={synMap[r.q] || ""} onChange={(e) => setSynMap((m) => ({ ...m, [r.q]: e.target.value }))} placeholder="map to an existing term…" data-testid={`syn-input-${r.q}`} className="h-7 flex-1 rounded-md border border-slate-200 px-2 text-xs" onKeyDown={(e) => { if (e.key === "Enter") suggestSynonym(r.q); }} />
                <button onClick={() => suggestSynonym(r.q)} disabled={savingSyn === r.q} data-testid={`syn-save-${r.q}`} className="inline-flex items-center gap-1 rounded-md bg-violet-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-violet-700 disabled:opacity-50"><Check className="h-3 w-3" /> Map</button>
              </div>
            </div>
          )) : <p className="text-sm text-slate-400">No empty searches in this range — every query found something. 🎉</p>}
        </div>
      </div>

      {/* Recent searches table */}
      <div className="card-soft p-5" data-testid="recent-searches-table">
        <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800"><Clock className="h-4 w-4 text-blue-500" /> Recent searches {live && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-600">LIVE</span>}</p>
        {(d.recent || []).length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="text-[10px] uppercase tracking-wide text-slate-400"><tr><th className="pb-2 pr-3">Query</th><th className="pb-2 pr-3">User</th><th className="pb-2 pr-3">Country</th><th className="pb-2 pr-3">Results</th><th className="pb-2">When</th></tr></thead>
              <tbody className="divide-y divide-slate-50">
                {d.recent.map((r, i) => (
                  <tr key={i} data-testid={`recent-search-row-${i}`}>
                    <td className="py-1.5 pr-3 font-medium text-slate-800">{r.q}</td>
                    <td className="py-1.5 pr-3 text-slate-500"><span className="inline-flex items-center gap-1">{r.user_name ? <User className="h-3 w-3 text-slate-400" /> : null}{r.user_name || <span className="text-slate-400">Guest</span>}</span></td>
                    <td className="py-1.5 pr-3 text-slate-500"><span className="inline-flex items-center gap-1"><Globe className="h-3 w-3 text-slate-300" />{r.country || "—"}</span></td>
                    <td className="py-1.5 pr-3">{r.no_result ? <span className="rounded bg-rose-50 px-1.5 py-0.5 text-xs font-bold text-rose-600">0</span> : <span className="font-semibold text-slate-700">{r.results}</span>}</td>
                    <td className="py-1.5 text-xs text-slate-400">{timeAgo(r.at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="text-sm text-slate-400">No searches logged in this range.</p>}
      </div>
    </div>
  );
}

function Configuration() {
  const [syn, setSyn] = useState([]);
  const [typo, setTypo] = useState([]);
  const [featured, setFeatured] = useState("");
  const [analytics, setAnalytics] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/marketplace/admin/search-config").then((r) => {
      setSyn(Object.entries(r.data.synonyms || {}).map(([k, v]) => ({ k, v })));
      setTypo(Object.entries(r.data.typos || {}).map(([k, v]) => ({ k, v })));
      setFeatured((r.data.featured_terms || []).join(", "));
    }).catch((e) => toast.error(formatApiError(e?.response?.data?.detail)));
    api.get("/marketplace/admin/search-analytics").then((r) => setAnalytics(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const synonyms = {}; syn.forEach((r) => { if (r.k && r.v) synonyms[r.k] = r.v; });
      const typos = {}; typo.forEach((r) => { if (r.k && r.v) typos[r.k] = r.v; });
      const featured_terms = featured.split(",").map((s) => s.trim()).filter(Boolean);
      await api.put("/marketplace/admin/search-config", { synonyms, typos, featured_terms });
      toast.success("Search configuration saved");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="search-config-tab">
      <div className="mb-3 flex justify-end">
        <button onClick={save} disabled={saving} data-testid="search-config-save" className="inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-5 py-2.5 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-60"><Save className="h-4 w-4" /> {saving ? "Saving…" : "Save"}</button>
      </div>
      {analytics && (
        <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="search-analytics">
          {[["Total searches", analytics.total_searches], ["AI searches", analytics.ai_searches], ["Result clicks", analytics.clicks], ["Added to cart", analytics.carts]].map(([l, v]) => (
            <div key={l} className="card-soft p-4"><p className="text-xs text-slate-500">{l}</p><p className="text-2xl font-bold text-slate-800">{v}</p></div>
          ))}
        </div>
      )}
      <div className="mb-4 grid gap-4 lg:grid-cols-2">
        <MapEditor title="Synonyms" hint="Map a term to equivalents (comma/slash separated), e.g. mobile → smartphone." rows={syn} setRows={setSyn} kLabel="term" vLabel="smartphone, cellphone" testid="synonyms" />
        <MapEditor title="Typo mappings" hint="Correct common misspellings, e.g. headphons → headphones." rows={typo} setRows={setTypo} kLabel="headphons" vLabel="headphones" testid="typos" />
      </div>
      <div className="card-soft p-5">
        <p className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-800"><Sparkles className="h-4 w-4 text-violet-500" /> Featured search terms</p>
        <p className="mb-2 text-xs text-slate-500">Comma-separated terms surfaced as suggestions.</p>
        <Input value={featured} onChange={(e) => setFeatured(e.target.value)} placeholder="coffee, running shoes, headphones" data-testid="featured-terms" />
      </div>
    </div>
  );
}

export default function AdminSearch() {
  const [tab, setTab] = useState("analytics");
  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader title="Search Management" icon={Search}
        subtitle="Track what shoppers search for and govern the ONE marketplace search engine — analytics, synonyms, typo mappings, featured terms and market-gap insights." />
      <div className="mb-4 inline-flex rounded-full border border-slate-200 bg-white p-1" data-testid="search-tabs">
        <button onClick={() => setTab("analytics")} data-testid="tab-analytics" className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition ${tab === "analytics" ? "brand-gradient-bg text-white" : "text-slate-600 hover:bg-slate-50"}`}><BarChart3 className="h-4 w-4" /> Search Analytics</button>
        <button onClick={() => setTab("config")} data-testid="tab-config" className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition ${tab === "config" ? "brand-gradient-bg text-white" : "text-slate-600 hover:bg-slate-50"}`}><Settings2 className="h-4 w-4" /> Configuration</button>
      </div>
      {tab === "analytics" ? <SearchAnalytics /> : <Configuration />}
    </div>
  );
}
